import { useParams } from '@tanstack/react-router';
import { IComment } from '_/common/types/comments.types';
import NotExists from '_/components/common/NotExists';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useDelivery, useDeliveryStats, useDeliveryComments, useDeliveryTransitions } from '_/hooks/useDeliveries';
import { IDelivery, IDeliveryStats, IDeliveryTransition } from '_/types/deliveries/delivery.types';
import React, { createContext, useContext, ReactNode } from 'react';

interface DeliveryContextValue {
  deliveryId: string | undefined;
  selectedDelivery: IDelivery;
  deliveryStats: IDeliveryStats;
  deliveryComments: IComment[];
  deliveryTransitions: IDeliveryTransition[];
  isLoading: boolean;
  isLoadingStats: boolean;
  isLoadingComments: boolean;
  isLoadingTransitions: boolean;
  isFetchingDelivery: boolean;
  isFetchingStats: boolean;
  isFetchingComments: boolean;
  isFetchingTransitions: boolean;
  notFound: boolean;
  refetchAll: () => void;
  refetchDelivery: () => void;
  refetchStats: () => void;
  refetchComments: () => void;
  refetchTransitions: () => void;
}

const DeliveryContext = createContext<DeliveryContextValue | undefined>(undefined);

export function DeliveryProvider({ children }: { children: ReactNode }) {
  const params = useParams({ strict: false }) as Partial<{ deliveryId?: string }>;
  const deliveryId = params.deliveryId || '';
  const { selectedOrgSlug } = useOrganization();

  const { data: selectedDelivery, isLoading, isFetching: isFetchingDelivery, refetch: refetchDelivery } = useDelivery(deliveryId);
  const {
    data: deliveryStats,
    isLoading: isLoadingStats,
    isFetching: isFetchingStats,
    refetch: refetchStats,
  } = useDeliveryStats(deliveryId, selectedDelivery?.status);
  const {
    data: deliveryComments,
    isLoading: isLoadingComments,
    isFetching: isFetchingComments,
    refetch: refetchComments,
  } = useDeliveryComments(deliveryId);
  const {
    data: deliveryTransitions,
    isLoading: isLoadingTransitions,
    isFetching: isFetchingTransitions,
    refetch: refetchTransitions,
  } = useDeliveryTransitions(deliveryId);

  const refetchAll = () => {
    void refetchDelivery();
    void refetchStats();
    void refetchComments();
    void refetchTransitions();
  };

  // Determine if delivery is not found: when loading is done, we have a deliveryId, but no matching delivery
  const notFound = !isLoading && !!deliveryId && !selectedDelivery;

  const value: DeliveryContextValue = {
    deliveryId: deliveryId,
    selectedDelivery: selectedDelivery || null,
    deliveryStats: deliveryStats || null,
    deliveryComments: deliveryComments || [],
    deliveryTransitions: deliveryTransitions || [],
    isLoading: isLoading,
    isLoadingStats: isLoadingStats,
    isLoadingComments: isLoadingComments,
    isLoadingTransitions: isLoadingTransitions,
    isFetchingDelivery: isFetchingDelivery,
    isFetchingStats: isFetchingStats,
    isFetchingComments: isFetchingComments,
    isFetchingTransitions: isFetchingTransitions,
    notFound: notFound,
    refetchAll: refetchAll,
    refetchDelivery: refetchDelivery,
    refetchStats: refetchStats,
    refetchComments: refetchComments,
    refetchTransitions: refetchTransitions,
  };

  // If delivery not found, render NotExists page
  if (notFound) {
    return <NotExists entityType="Delivery" backUrl={`/orgs/${selectedOrgSlug}/deliveries`} backLabel="Back to Deliveries" />;
  }

  return <DeliveryContext.Provider value={value}>{children}</DeliveryContext.Provider>;
}

export function useDeliveryContext(): DeliveryContextValue {
  const context = useContext(DeliveryContext);
  if (context === undefined) {
    throw new Error('useDeliveryContext must be used within a DeliveryProvider');
  }
  return context;
}
